import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ChevronUp, ChevronDown, Save, Sparkles, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const METRIC_LABELS = {
  members: 'Members',
  new_members_week: 'New this week',
  posts_week: 'Posts this week',
  events_month: 'Events this month',
};

export default function ClubPulseSettings() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [rows, setRows] = useState([]); // [{key, visible, customLabel}]
  const [clubName, setClubName] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/community/clubs/${slug}/pulse-settings`);
        const s = res.data.settings;
        setClubName(res.data.club_name);
        setEnabled(s.enabled);
        const available = res.data.available_metrics;
        const order = s.metric_order?.length ? s.metric_order : available;
        const ordered = [...order, ...available.filter((k) => !order.includes(k))];
        setRows(
          ordered.map((key) => ({
            key,
            visible: s.visible_metrics.includes(key),
            customLabel: s.custom_labels?.[key] || '',
          })),
        );
      } catch (err) {
        if (err.response?.status === 403) {
          toast.error('You need club admin rights to access this page');
          navigate(`/club/${slug}`);
        } else {
          toast.error('Failed to load settings');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, navigate]);

  const move = (idx, dir) => {
    const next = [...rows];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setRows(next);
  };

  const toggleVisible = (idx) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, visible: !r.visible } : r)));
  };

  const setLabel = (idx, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, customLabel: value } : r)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const visible = rows.filter((r) => r.visible).map((r) => r.key);
      const order = rows.map((r) => r.key);
      const custom = Object.fromEntries(
        rows.filter((r) => r.customLabel.trim()).map((r) => [r.key, r.customLabel.trim()]),
      );
      await axios.put(`${API}/api/community/clubs/${slug}/pulse-settings`, {
        enabled,
        visible_metrics: visible,
        metric_order: order,
        custom_labels: custom,
      });
      toast.success('Pulse settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-10 text-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-10 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold flex items-center gap-2" data-testid="club-pulse-settings-title">
          <Sparkles className="w-7 h-7 text-amber-500" />
          Pulse Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Customize the community pulse for <strong>{clubName}</strong>
        </p>
      </div>

      <Card className="mb-4">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Show Pulse on Club Pages</CardTitle>
            <CardDescription className="text-xs">
              Turn off to hide the banner entirely for this club
            </CardDescription>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="pulse-enabled-switch"
            aria-label="Enable pulse"
          />
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Metrics</CardTitle>
          <CardDescription className="text-xs">
            Toggle visibility, reorder (↑↓), and optionally rename each metric
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {rows.map((r, i) => (
            <div
              key={r.key}
              className="flex items-center gap-2 p-2.5 border rounded-md"
              data-testid={`metric-row-${r.key}`}
            >
              <div className="flex flex-col gap-0.5">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                  disabled={i === 0}
                  aria-label="Move up"
                  data-testid={`move-up-${r.key}`}
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                  disabled={i === rows.length - 1}
                  aria-label="Move down"
                  data-testid={`move-down-${r.key}`}
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </div>

              <Switch
                checked={r.visible}
                onCheckedChange={() => toggleVisible(i)}
                data-testid={`visibility-switch-${r.key}`}
                aria-label={`Toggle ${r.key} visibility`}
              />

              <div className="flex-1 min-w-0">
                <Label className="text-xs text-muted-foreground">{METRIC_LABELS[r.key] || r.key}</Label>
                <Input
                  placeholder={`Custom label (optional, default: "${METRIC_LABELS[r.key] || r.key}")`}
                  value={r.customLabel}
                  onChange={(e) => setLabel(i, e.target.value)}
                  className="h-8 text-sm"
                  data-testid={`label-input-${r.key}`}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="mt-6 flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-pulse-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>
    </div>
  );
}
